import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { Layout, Menu, Drawer } from "antd";
import SubMenu from "antd/lib/menu/SubMenu";
import { NavLink, useHistory } from "react-router-dom";
import styles from "./Sidebar.module.css";
import { compact, noop } from "lodash";
import { useTheme } from "themes/useTheme";
import Logo from "./components/Logo";
import { usePermissions, useIsAdmin } from "components/CurrentUser/hooks";
import { authorize } from "routes";
import { useTranslation } from "react-i18next";

const { Sider } = Layout;

interface IProps {
  routes: any[];
  onDrawerClose: () => void;
  drawerVisible: boolean;
  pathName: string;
}

const SidebarMobile = (props: IProps) => {
  const { routes, onDrawerClose = noop, drawerVisible, pathName } = props;
  const { push } = useHistory();
  const { themeVariables } = useTheme();

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      onDrawerClose();
    });
    return () => cancelAnimationFrame(frameId);
  }, [pathName, onDrawerClose]);

  const [openKeys, setOpenKeys] = useState([`/${pathName.split("/")[1]}`]);

  const openKeysRef = useRef<string[]>([]);

  useEffect(() => {
    const animationFrameId = requestAnimationFrame(() => {
      const arrPathname = pathName.split("/");
      const openKeys = compact(arrPathname).reduce(
        (acc: string[], path: string, index) => {
          if (index === 0) acc.push(`/${path}`);
          if (index > 0) acc.push(`${acc[index - 1]}/${path}`);
          return acc;
        },
        []
      );
      setOpenKeys(openKeys);
      openKeysRef.current = openKeys;
    });
    return () => cancelAnimationFrame(animationFrameId);
  }, [pathName]);

  const handleSubMenuClick = useCallback(
    ({ key, linkTo }: any) => {
      if (linkTo) {
        push(linkTo);
      }
      if (openKeysRef.current.includes(key)) {
        const keys = openKeysRef.current.filter((k) => k !== key);
        openKeysRef.current = keys;
        setOpenKeys(keys);
      } else {
        openKeysRef.current = [...openKeysRef.current, key];
        setOpenKeys(openKeysRef.current);
      }
    },
    [push]
  );

  const permissions = usePermissions();

  const isAdmin = useIsAdmin();

  const selectedKeys = useMemo(
    () =>
      compact(pathName.split("/")).reduce(
        (acc: string[], path: string, index) => {
          if (index === 0) acc.push(`/${path}`);
          if (index > 0) acc.push(`${acc[index - 1]}/${path}`);
          return acc;
        },
        []
      ),
    [pathName]
  );

  const { t } = useTranslation();

  const renderMenuItem = (routes: any[]) => {
    return routes.map((route: any) => {
      const {
        name: _name,
        path,
        hideInMenu = false,
        icon: Icon,
        permissions: _permissions,
      } = route;
      if (hideInMenu || !authorize(permissions, _permissions, isAdmin))
        return null;
      const routes = (route.routes || []).filter((r: any) => !r.hideInMenu);
      if (!routes.length) {
        if (!route.name) return null;
        // @ts-ignore
        const name = t(_name as any);
        return (
          <Menu.Item key={path}>
            <NavLink to={path}>
              <span style={{ userSelect: "none" }}>
                {!!Icon && <Icon />}
                <span style={{ fontWeight: 500 }}>{name}</span>
              </span>
            </NavLink>
          </Menu.Item>
        );
      } else {
        const name = t(_name as any);
        return (
          <SubMenu
            onTitleClick={({ key }) =>
              handleSubMenuClick({ key, linkTo: route.linkTo })
            }
            key={path}
            title={
              <span style={{ userSelect: "none" }}>
                {!!Icon && <Icon />}
                <span style={{ fontWeight: 500 }}>{name}</span>
              </span>
            }
          >
            {renderMenuItem(route.routes)}
          </SubMenu>
        );
      }
    });
  };

  return (
    <Drawer
      bodyStyle={{
        padding: 0,
        backgroundColor: themeVariables.background_color,
      }}
      width={200}
      visible={drawerVisible}
      placement="left"
      onClose={onDrawerClose}
    >
      <Sider
        width={200}
        trigger={null}
        collapsible={false}
        collapsed={false}
        className={styles["sidebar"]}
        style={{
          backgroundColor: themeVariables.background_color,
        }}
      >
        <Logo />
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          style={{ borderWidth: 0 }}
          openKeys={openKeys}
        >
          {renderMenuItem(routes)}
        </Menu>
      </Sider>
    </Drawer>
  );
};

export default React.memo(SidebarMobile);
